import PouchDB from "pouchdb-browser";
import { localPouchPrefix } from "../config";
import { logger } from "../functions/logger";

export default function usePouch(dbName) {
  const getDB = () => {
    return new PouchDB(dbName);
  };

  const getAllDocIds = async (options) => {
    try {
      const res = await getDB().allDocs({ include_docs: false, ...options });
      return res.rows.filter((x) => !x.deleted);
    } catch (err) {
      console.log("Error fetching docs ID", err);
      return [];
    }
  };

  const getAllDocs = async (options) => {
    try {
      const result = await getDB().allDocs({ include_docs: true, ...options });
      const docs = result.rows.map((row) => row.doc);

      if (options?.include_deleted) return docs;
      return docs.filter((x) => !x.deleted);
    } catch (error) {
      console.error("Error fetching all docs:", error);
      return [];
    }
  };

  const getDocById = async (docId, options = {}) => {
    try {
      return await getDB().get(docId, options);
    } catch (err) {
      // console.log("Error while fetching single Doc:", err);
      return null;
    }
  };

  const putDoc = async (doc, options = {}) => {
    try {
      //TODO: Temporary adding this prop for debugging purpose.
      const prefix = localStorage.getItem(localPouchPrefix);

      if (prefix) {
        doc.prefix = prefix;
      }

      const putRes = await getDB().put(doc, options);

      logger.info(`${dbName}.saved`, "Document saved", { entity: dbName, entityId: doc?._id });
      return putRes;
    } catch (err) {
      logger.error(`${dbName}.save_failed`, "Error saving document", {
        entity: dbName,
        entityId: doc?._id,
        context: { error: String(err?.message || err) },
      });
      console.log("Error in putDoc function:", err);
      throw new Error(err);
    }
  };

  const putSert = async (data, options = {}) => {
    try {
      const doc = await getDocById(data._id);
      const res = doc
        ? await getDB().put({ ...doc, ...data }, options)
        : await getDB().put(data, options);

      logger.info(`${dbName}.saved`, "Document saved", { entity: dbName, entityId: data?._id });
      return res;
    } catch (err) {
      logger.error(`${dbName}.save_failed`, "Error saving document", {
        entity: dbName,
        entityId: data?._id,
        context: { error: String(err?.message || err) },
      });
      console.log("Error in putSert function", err);
      throw new Error(err);
    }
  };

  const bulkDocs = async (docs, options = {}) => {
    try {
      const result = await getDB().bulkDocs(docs, options);
      logger.info(`${dbName}.bulk_saved`, "Bulk documents saved", {
        entity: dbName,
        context: { count: Array.isArray(docs) ? docs.length : undefined },
      });
      return result;
    } catch (err) {
      logger.error(`${dbName}.bulk_save_failed`, "Error saving bulk documents", {
        entity: dbName,
        context: { error: String(err?.message || err) },
      });
      console.log("Error in bulkDocs function", err);
      return err;
    }
  };

  const deleteDoc = async (docId) => {
    try {
      const doc = await getDB().get(docId);

      doc.deleted = true;
      const result = await getDB().put(doc);
      logger.warning(`${dbName}.deleted`, "Document deleted", { entity: dbName, entityId: docId });
      return result;
    } catch (err) {
      logger.error(`${dbName}.delete_failed`, "Error deleting document", {
        entity: dbName,
        entityId: docId,
        context: { error: String(err?.message || err) },
      });
      console.log("Error while deleting doc:", err);
      throw new Error(err);
    }
  };

  const getDatabaseInfo = async () => {
    try {
      return await getDB().info();
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  const compact = async () => {
    try {
      return await getDB().compact();
    } catch (err) {
      console.log(err);
    }
  };

  const deleteDatabase = async () => {
    try {
      return await getDB().destroy();
    } catch (err) {
      console.log(err);
      return err;
    }
  };

  return {
    getDocById,
    getAllDocIds,
    getAllDocs,
    putDoc,
    putSert,
    compact,
    deleteDoc,
    bulkDocs,
    getDatabaseInfo,
    deleteDatabase,
  };
}
